import { bottom } from "@popperjs/core";
import { COLOR_PALETTE } from "@shared/constants/colorPalette";
import { PERMISSIONS } from "@shared/constants/permissions";
import AiSparklesIcon from "@shared/svg/ai-sparkles.svg?react";
import DeleteIcon from "@shared/svg/delete-document.svg?react";
import InternalMachineHistoryNoteIcon from "@shared/svg/internal-machine-history-note.svg?react";
import { AlertBox } from "@shared/ui/AlertBox";
import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import { BUTTON_VARIANTS } from "@shared/ui/Button";
import Headline, { HEADLINE_SIZES } from "@shared/ui/Headline";
import {
  ExternalIcon,
  EXTERNAL_ICON_SIZES,
  EXTERNAL_ICON_THUMB_BADGE_CLASS,
} from "@shared/ui/Indicators";
import Label, { LABEL_SIZES } from "@shared/ui/Label";
import Overlay from "@shared/ui/Overlay";
import Tag, { TAG_COLORS } from "@shared/ui/Tag";
import TextEditor from "@shared/ui/TextEditor";
import Toast, { TOAST_TYPES } from "@shared/ui/Toast";
import Tooltip from "@shared/ui/Tooltip";
import { WorkOrderAssetsTooltipContent } from "@shared/ui/WorkOrderCard";
import { Role } from "@shared/types/user";
import { isArray } from "lodash";
import moment from "moment-timezone";
import {
  ArrowsInSimple,
  ArrowsOutSimple,
  DotsThree,
  PencilSimple,
  Plus,
  Trash,
} from "phosphor-react";
import React, { Fragment, useMemo, useState, useRef, useEffect } from "react";
import { useIntl } from "react-intl";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Waypoint } from "react-waypoint";
import { AssetHierarchySelect } from "./AssetHierarchySelect";

import HISTORY_TYPES from "$/settings/enums/history/machineHistory.json";
import { useChatStore } from "~/stores/chatStore";
import EmptySpace from "~/components/_emptySpace";
import { PopupMenuV2 } from "~/components/_popupMenu";
import { useAuth } from "~/components/general";
import WorkOrderTypeIcons from "~/components/tickets/WorkOrderTypeIcons";
import {
  COLOR,
  ITEMS_BEFORE_PAGE,
  ITEMS_PER_PAGE,
  WORK_ORDER_TYPE_ICONS,
} from "~/constants";
import { MachineHistoryLoading } from "~/containers/machine/loader/MachineHistoryLoading";
import { useUploadFiles } from "~/hooks";
import { useRoleManager } from "~/hooks/_useRoleManager";
import usePermissions from "~/hooks/usePermissions";
import { PdfColoredIcon, SidebarMachinesIcon } from "~/icons/icons";
import {
  useCreateMachineHistoryNote,
  useDeleteMachineHistoryNote,
  useUpdateMachineHistoryNote,
  useListOwnOemMachineHistory,
  useListOwnOemSharedAssetHistory,
} from "~/services/asset";
import { getEnums, pluralizeCount } from "~/utils";
import { generateStatusBackgroundColor } from "~/utils/_colors";
import { trimText } from "~/utils/_initials";

const HISTORY_TYPES_ENUMS = getEnums(HISTORY_TYPES, "reference");

// `.month-wise-ticket-wrapper a` in _item-list.scss carries the card chrome, so
// rows rendered as anything other than an anchor have to restate it.
const HISTORY_CARD_STYLES = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  width: "100%",
  padding: "12px",
  marginBottom: "12px",
  border: "1px solid #f3f4f6",
  borderRadius: "8px",
};

export const MachineHistory = ({
  machine,
  isSharedAsset = false,
  onRequestClick,
}) => {
  const { messages } = useIntl();
  const { user } = useAuth();
  const { can } = usePermissions();
  const { months } = messages["common"];
  const noteText = useRef("");
  const deleteNoteId = useRef("");
  const [showAddNote, setShowAddNote] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [selectedMachineIds, setSelectedMachineIds] = useState(
    machine?._id ? [machine._id] : [],
  );

  const dateFormat = user?.dateFormat || "DD.MM.YYYY";

  const fileUploader = useUploadFiles();

  const { mutation: createMachineHistoryNote } = useCreateMachineHistoryNote();
  const { removeHistoryNote, loading: deletingNote } =
    useDeleteMachineHistoryNote();

  const {
    history: rawHistory,
    loading,
    totalCount,
    ticketCount,
    supportTicketCount,
    handleFetchMore,
    hierarchyAssets = [],
  } = (
    isSharedAsset
      ? useListOwnOemSharedAssetHistory
      : useListOwnOemMachineHistory
  )({
    limit: ITEMS_PER_PAGE,
    skip: 0,
    includeHierarchy: true,
    where: {
      machine: machine?._id ?? null,
    },
    selectedMachineIds,
  });

  const assetList = useMemo(() => {
    if (hierarchyAssets.length > 0) {
      return hierarchyAssets.map((item) => ({
        id: item?._id,
        label: item?.name,
        level: item?.level,
      }));
    }

    // Fallback for shared asset history which may not return hierarchyAssets yet
    const listData =
      rawHistory
        ?.map((item) => ({
          id: item?.machine,
          label: item?.machineDetails?.name,
          level: item?.level,
        }))
        ?.filter(
          (item, index, self) =>
            item.id && index === self.findIndex((t) => t.id === item.id),
        ) || [];

    return listData;
  }, [hierarchyAssets, rawHistory]);

  useEffect(() => {
    if (machine?._id) {
      setSelectedMachineIds([machine._id]);
    }
  }, [machine?._id]);

  const history = useMemo(() => {
    return rawHistory.filter((item) =>
      selectedMachineIds.includes(item?.machine),
    );
  }, [rawHistory, selectedMachineIds]);

  const displayedTicketCount = ticketCount ?? 0;

  // Both counts are scoped to the same asset ids as the history list (including
  // the hierarchy multi-select) and count distinct tickets, not history rows.
  // `supportTicketCount` is nullable — fall back to the work-order-only label so
  // the header stays correct if the field isn't resolved.
  const relatedCountLabel = useMemo(() => {
    const isMultiple = selectedMachineIds.length > 1;
    const assetMessages = messages?.hierarchy?.assets ?? {};

    if (supportTicketCount == null) {
      return `${displayedTicketCount} ${
        assetMessages[isMultiple ? "ticketsRelatedMultiple" : "ticketsRelated"]
      }`;
    }

    return assetMessages[
      isMultiple
        ? "ticketsAndSupportTicketsRelatedMultiple"
        : "ticketsAndSupportTicketsRelated"
    ]
      ?.replace(
        "{workOrders}",
        pluralizeCount(
          displayedTicketCount,
          assetMessages.workOrderUnit,
          assetMessages.workOrderUnitPlural,
        ),
      )
      ?.replace(
        "{supportTickets}",
        pluralizeCount(
          supportTicketCount,
          assetMessages.supportTicketUnit,
          assetMessages.supportTicketUnitPlural,
        ),
      );
  }, [
    displayedTicketCount,
    supportTicketCount,
    selectedMachineIds.length,
    messages,
  ]);

  const handleScrollBottom = (event) => {
    if (
      history.length >= ITEMS_PER_PAGE &&
      !loading &&
      event?.previousPosition !== "above" &&
      totalCount > history.length
    ) {
      handleFetchMore({
        limit: ITEMS_PER_PAGE,
        skip: history.length,
      });
    }
  };

  const objectIdsToShowMonthWith = useMemo(() => {
    let currentMonth = -1;
    let currentYear = -1;

    return history
      .filter((item) => {
        if (item.month !== currentMonth || item.year !== currentYear) {
          currentMonth = item.month;
          currentYear = item.year;
          return true;
        }
        return false;
      })
      .map((item) => item._id);
  }, [history]);

  const processAttachment = async (attachments) => {
    const toastId = "uploading-attachments";
    toast(<Toast message={messages?.procedures.uploadingFieldAttachments} />, {
      closeButton: false,
      autoClose: false,
      toastId,
    });
    const files = [...attachments];

    if (!files || !isArray(files)) return [];
    const list = [];

    for await (const file of files) {
      if (file?.url) {
        list.push(file);
        continue;
      }

      try {
        const fileObj = { name: file.name, size: file.size, type: file.type };
        const uploadData = {
          folder: `oem/${machine?.oem?.slug}/machine/${
            machine?.slug ?? machine.serialNumber
          }/note`,
          files: [file],
          forceCustomPath: true,
        };

        const { urls } = await fileUploader(uploadData, false);
        if (urls[0]) {
          fileObj["url"] = urls[0];
          list.push(fileObj);
        }
      } catch (error) {
        //
      }
    }

    toast.update(toastId, {
      render: <Toast message={messages?.common.upload.success} />,
      autoClose: true,
    });

    return list;
  };

  const handleHistorySave = async (attachments, isCheckboxChecked) => {
    try {
      setIsSavingNote(true);
      let attachmentLinks = [];
      if (attachments.length) {
        attachmentLinks = await processAttachment(attachments);
      }
      await createMachineHistoryNote({
        note: {
          message: JSON.stringify(noteText.current),
          attachments: attachmentLinks,
          isInternal: !isCheckboxChecked,
        },
        machine: machine?._id,
      });
      setShowAddNote(false);
      toast(
        <Toast
          message={
            messages?.machines?.machineDetails.historyTab.noteMessages
              .createSuccess
          }
        />,
        {
          closeButton: false,
        },
      );
    } catch (error) {
      toast(<Toast message={error.message} type={TOAST_TYPES.ERROR} />, {
        closeButton: false,
      });
    } finally {
      setIsSavingNote(false);
    }
  };

  const getUrl = (item, isSharedAsset) => {
    switch (item.type) {
      case HISTORY_TYPES_ENUMS.FormSubmission:
        return item.url;
      case HISTORY_TYPES_ENUMS.Procedure:
        return isSharedAsset
          ? null
          : `/app/work/work-orders/${item?.ticket?._id}/procedure/${item?.resourceId}`;
      case HISTORY_TYPES_ENUMS.SupportTicket:
        return `/app/work/tickets/${item?.resourceId}`;
      default:
        return isSharedAsset
          ? `/app/asset-hub/requests/${item?.resourceId}`
          : `/app/work/work-orders/${item?.resourceId}`;
    }
  };

  const prepareType = (history) => {
    switch (history.type) {
      case HISTORY_TYPES_ENUMS.Procedure:
        return {
          icon: WORK_ORDER_TYPE_ICONS.PROCEDURE,
          color: COLOR_PALETTE.red.primary,
          isInternal: !!history.ticket?.ticketType?.isInternal,
        };

      case HISTORY_TYPES_ENUMS.FormSubmission:
        return {
          icon: WORK_ORDER_TYPE_ICONS.FORM_SUBMISSION,
          color: COLOR_PALETTE.gray.primary,
          isInternal: history.ticket?.ticketType?.isInternal,
        };

      case HISTORY_TYPES_ENUMS.SupportTicket:
        // Support tickets carry their type on `resource.type` and are
        // configured under the OEM's `supportTicketTypes` (icon/color/isInternal).
        return (
          user?.oem?.supportTicketTypes?.find(
            (ticketType) => history.resource?.type === ticketType._id,
          ) || {}
        );

      default:
        return (
          user?.oem?.ticketTypes?.find(
            (ticketType) => history.resource?.ticketType === ticketType._id,
          ) || {}
        );
    }
  };

  const handleDeleteNote = async () => {
    try {
      await removeHistoryNote(deleteNoteId.current, machine._id);
      toast(
        <Toast
          message={
            messages?.machines?.machineDetails.historyTab.noteMessages
              .deleteSuccess
          }
        />,
        {
          closeButton: false,
        },
      );
    } catch (error) {
      toast(<Toast message={error.message} type={TOAST_TYPES.ERROR} />, {
        closeButton: false,
      });
    } finally {
      deleteNoteId.current = "";
      setShowDeleteAlert(false);
    }
  };

  if (loading && !history.length) {
    return <MachineHistoryLoading />;
  }

  return (
    <>
      {!isSharedAsset && can(PERMISSIONS.assets.updateHistory) && (
        <div className="u-flex u-width-100 u-margin-b-2">
          <div className="add-machine-note-text-editor-container">
            {!showAddNote ? (
              <div
                className="add-machine-note-placeholder-container u-cursor-pointer"
                onClick={() => setShowAddNote(true)}
              >
                <BodyText size={BODY_TEXT_SIZES.X_SMALL} color="text-secondary">
                  {messages.machines.machineDetails.historyTab.addNote}
                </BodyText>
              </div>
            ) : (
              <>
                <div className="u-width-100 u-relative">
                  <TextEditor
                    keyId="add-note-detail"
                    placeholder={
                      messages.machines.machineDetails.historyTab.addNote
                    }
                    className="flex flex-col-reverse w-full"
                    toolbarTopBorder
                    showAttachmentButton
                    showActionButtons
                    autoFocus={true}
                    onUpdate={(newValue) => (noteText.current = newValue)}
                    handleClose={() => setShowAddNote(false)}
                    isSaving={isSavingNote}
                    handleSave={handleHistorySave}
                    checkboxLabel={
                      messages?.machines?.machineDetails.historyTab
                        .showToFacility
                    }
                    defaultCheckboxValue={true}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <div className="flex justify-between items-end my-3">
        <p className="c-infoNote text-sm">{relatedCountLabel}</p>
        <div>
          <AssetHierarchySelect
            onUpdate={(selectedIds) => {
              setSelectedMachineIds(selectedIds);
            }}
            assetList={assetList}
            currentAssetId={machine?._id}
            initialSelectedIds={selectedMachineIds}
          />
        </div>
      </div>
      {history.map((item, index) => {
        const url = getUrl(item, isSharedAsset);
        // Support tickets resolve their status from `supportTicketStatuses`;
        // work orders (and everything else) from `statuses`.
        const statusList =
          item?.type === HISTORY_TYPES_ENUMS.SupportTicket
            ? user?.oem?.supportTicketStatuses
            : user?.oem?.statuses;
        const status =
          statusList?.find(
            (statusItem) => item?.resource?.status === statusItem._id,
          ) || {};
        const type = prepareType(item);
        return (
          <Fragment key={item._id}>
            <div className="month-wise-ticket-wrapper" key={index}>
              {objectIdsToShowMonthWith.includes(item._id) && (
                <div className="absolute top-0 left-0 flex flex-col justify-center w-7 py-2xs bg-primary">
                  <BodyText
                    size={BODY_TEXT_SIZES.X_SMALL}
                    className="text-center"
                  >
                    {months[item?.month]}
                  </BodyText>{" "}
                  <Label
                    size={LABEL_SIZES.X_SMALL}
                    color="text-secondary"
                    className="text-center w-full font-medium"
                  >
                    {item?.year}
                  </Label>
                </div>
              )}
              {[HISTORY_TYPES_ENUMS.AI, HISTORY_TYPES_ENUMS.Note].includes(
                item.type,
              ) ? (
                <HistoryNoteItem
                  item={item}
                  msg={messages}
                  onDelete={() => {
                    deleteNoteId.current = item._id;
                    setShowDeleteAlert(true);
                  }}
                  processAttachment={processAttachment}
                  machine={machine}
                  isSharedAsset={isSharedAsset}
                />
              ) : (
                <MachineHistoryItem
                  item={item}
                  status={status}
                  type={type}
                  url={url}
                  isSharedAsset={isSharedAsset}
                  machine={machine}
                  onRequestClick={onRequestClick}
                />
              )}
            </div>
            {!loading &&
              totalCount > history.length &&
              index === history.length - ITEMS_BEFORE_PAGE && (
                <Waypoint onEnter={handleScrollBottom} />
              )}
          </Fragment>
        );
      })}
      {history.length > 0 && loading && <MachineHistoryLoading />}
      {totalCount === history.length && (
        <>
          <div className="month-wise-ticket-wrapper machine-info">
            <div className="absolute top-0 left-0 flex flex-col justify-center w-7 bg-primary">
              <BodyText size={BODY_TEXT_SIZES.X_SMALL} className="text-center">
                {months[new Date(machine?.createdAt).getMonth()]}
              </BodyText>{" "}
              <Label
                size={LABEL_SIZES.X_SMALL}
                color="text-secondary"
                className="text-center w-full font-medium"
              >
                {new Date(machine?.createdAt).getFullYear()}
              </Label>
            </div>
            <Link className="pointer-events-none">
              <div className="ticket-machine-info">
                <span className="machine-icon">
                  <SidebarMachinesIcon />
                </span>
                <span>
                  {messages["hierarchy"]["assets"]["creationDate"]} &nbsp;
                </span>{" "}
                <span className="machine-creation-date">
                  {moment(machine?.createdAt).format(dateFormat)}
                </span>
              </div>
            </Link>
          </div>
        </>
      )}

      <AlertBox
        title={
          messages?.machines?.machineDetails.historyTab.noteMessages.deleteAlert
            .title
        }
        description={
          messages?.machines?.machineDetails.historyTab.noteMessages.deleteAlert
            .message
        }
        isOpen={showDeleteAlert}
        acceptButtonText={messages?.common?.delete}
        acceptButtonVariant={BUTTON_VARIANTS.DANGER}
        cancelButtonText={messages?.common?.cancel}
        disableAcceptButton={deletingNote}
        image={<DeleteIcon width="130" height="130" />}
        onAccept={handleDeleteNote}
        onCancel={() => setShowDeleteAlert(false)}
        overlay={true}
      />
    </>
  );
};

export const MachineHistoryItem = ({
  item,
  status,
  url,
  type = {},
  machine,
  isSharedAsset = false,
  isConnectionHistory = false,
  onRequestClick,
}) => {
  const { messages } = useIntl();
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();

  const { user } = useAuth();

  const dateFormat = user?.dateFormat || "DD-MM-YYYY";

  // A work order can target multiple assets and own sub work orders. Surface
  // both in the meta line, mirroring the WorkOrderCard treatment.
  // Support tickets carry their own asset list on `supportTicketDetails`;
  // `ticketDetails` only ever resolves the work order relation.
  const isSupportTicket = item?.type === HISTORY_TYPES_ENUMS.SupportTicket;
  const ticketAssets =
    (isSupportTicket
      ? item?.supportTicketDetails?.assets
      : item?.ticketDetails?.assets) ?? [];
  const hasMultipleAssets = ticketAssets.length > 1;
  const assetsTooltipId = `history-assets-tooltip-${
    item?._id ?? item?.resourceId
  }`;
  const inaccessibleTooltipId = `history-inaccessible-tooltip-${
    item?._id ?? item?.resourceId
  }`;
  const primaryTicketAsset =
    ticketAssets?.[0] ?? item?.machineDetails ?? machine;
  const singleAssetName = primaryTicketAsset?.name;
  const singleAssetId = primaryTicketAsset?._id;
  const singleAssetSerial = primaryTicketAsset?.serialNumber;
  const singleAssetIsDeleted = primaryTicketAsset?.isDeleted;
  const singleAssetFallbackLabel = singleAssetIsDeleted
    ? messages?.hierarchy?.assets?.deletedAsset
    : messages?.tickets?.noAssetsAssigned;
  const subWorkOrderCount = isSupportTicket
    ? 0
    : item?.ticketDetails?.subWorkOrders?.length ?? 0;
  const subWorkOrderLabel =
    subWorkOrderCount > 1
      ? messages?.tickets?.subWorkOrders ?? "Sub Work Orders"
      : messages?.tickets?.subWorkOrder ?? "Sub Work Order";

  // A portal sees history for every asset it holds, including tickets raised by
  // other portals on the share chain — deliberate — but it can only open the one
  // that routes to it. Such a row stays complete and stops being a link.
  // `null`/`undefined` means there was nothing to open in the first place (notes,
  // procedures, AI rows) or a caller that doesn't select the field, so only an
  // explicit `false` takes the navigation away.
  const isResourceAccessible = item?.isResourceAccessible !== false;
  // The role must also be able to open the page the row links to (a work
  // order, ticket or procedure), by the same rule that page applies. External
  // links (form submission files) aren't app pages and stay open.
  const { canOpen } = usePermissions();
  const isPermitted = !url?.startsWith("/app/") || canOpen(url);

  const iconStyles = useMemo(() => {
    return {
      backgroundColor: isHovered
        ? type.color
        : generateStatusBackgroundColor(type?.color),
    };
  }, [isHovered, type.color]);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };
  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  // Handle click - use callback if provided for requests in shared assets context
  const handleClick = (e) => {
    // Handle form submissions and procedures as before
    if (
      (item?.type === HISTORY_TYPES_ENUMS.FormSubmission ||
        (item?.type === HISTORY_TYPES_ENUMS.Procedure && isSharedAsset)) &&
      (item?.resource.url || item?.resource.pdfUrl)
    ) {
      e.preventDefault();
      window.open(item?.resource.url || item?.resource.pdfUrl, "_blank");
      return;
    }

    // Use callback for request navigation if provided (customer-portal context)
    if (
      onRequestClick &&
      isSharedAsset &&
      item?.type !== HISTORY_TYPES_ENUMS.FormSubmission &&
      item?.type !== HISTORY_TYPES_ENUMS.Procedure
    ) {
      e.preventDefault();
      onRequestClick(
        item?.resourceId,
        item?.resource?.title || item?.resource?.name,
        isSupportTicket,
      );
    }
  };

  // Determine if we should render as a link, a div with onClick, or plain text
  const Wrapper =
    !isPermitted || !isResourceAccessible
      ? // The hover props are dropped on purpose: a row that cannot be opened
        // should not light up like one that can. The tooltip explains the
        // portal case only; a role without access just gets a plain row.
        ({ children, className }) =>
          isResourceAccessible ? (
            <div className={className} style={HISTORY_CARD_STYLES}>
              {children}
            </div>
          ) : (
            <div
              className={className}
              style={HISTORY_CARD_STYLES}
              data-for={inaccessibleTooltipId}
              data-tip={
                messages?.machines?.machineDetails?.historyTab
                  ?.resourceNotAccessible ??
                "Handled by another portal on this account. Shown here because it involves your asset."
              }
            >
              {children}
              <Tooltip
                id={inaccessibleTooltipId}
                className="w-[220px]"
                getContent={(dataTip) => dataTip}
                place="top"
              />
            </div>
          )
      : onRequestClick &&
        isSharedAsset &&
        item?.type !== HISTORY_TYPES_ENUMS.FormSubmission &&
        item?.type !== HISTORY_TYPES_ENUMS.Procedure
      ? ({ children, className, ...props }) => (
          <div
            {...props}
            className={className}
            style={{ ...HISTORY_CARD_STYLES, cursor: "pointer" }}
            onClick={handleClick}
          >
            {children}
          </div>
        )
      : ({ children, ...props }) => (
          <Link {...props} to={url} onClick={handleClick}>
            {children}
          </Link>
        );

  return (
    <Wrapper
      className="group"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="ticket-info-content">
        <span
          className={`ticket-icon history ${
            item?.type === HISTORY_TYPES_ENUMS.SupportTicket
              ? "!rounded-sm"
              : ""
          }`}
          style={iconStyles}
        >
          <WorkOrderTypeIcons
            size={16}
            icon={type.icon}
            strokeColor={isHovered ? COLOR.$white_v2 : type.color}
          />
          {type?.isInternal || item?.resource?.isInternal ? null : (
            <ExternalIcon
              size={EXTERNAL_ICON_SIZES.ACCORDION}
              className={EXTERNAL_ICON_THUMB_BADGE_CLASS}
            />
          )}
        </span>
        <div className="ticket-info">
          <Headline size={HEADLINE_SIZES.X_SMALL}>
            {trimText(item?.resource?.title ?? item?.resource?.name)}
          </Headline>
          {item?.type === HISTORY_TYPES_ENUMS.FormSubmission ? (
            <div className="ticket-meta">
              {(item?.customer?.name || item?.customer?.isDeleted) && (
                <>
                  <span className="meta">
                    <span className="meta-label">
                      {item?.customer?.name ||
                        messages?.tickets?.deletedFacility}
                    </span>
                  </span>
                  <span className="seperator"></span>
                </>
              )}
              <span className="meta">
                <span className="meta-label">
                  {item?.machineDetails?.name || machine?.name}
                </span>
              </span>
              <span className="seperator"></span>
              <span className="meta">
                <span className="meta-label">
                  {item?.machineDetails?.serialNumber || machine?.serialNumber}
                </span>
              </span>
            </div>
          ) : (
            <div className="ticket-meta flex-col">
              {item?.type === HISTORY_TYPES_ENUMS.Procedure && (
                <div className="flex w-full">
                  <span className="meta">
                    <span className="meta-label">
                      {messages?.facilities?.id}: {item?.resource.procedureId}
                    </span>
                    <span className="seperator"></span>
                    <div className="meta-label">
                      {messages?.procedures?.finalizedText}:{" "}
                      {moment(item?.resource.updatedAt).format(dateFormat)}
                    </div>
                  </span>
                </div>
              )}

              <div className="flex w-full">
                {" "}
                <span className="meta">
                  <span className="meta-label">
                    #{item?.resource?.id ?? item?.ticket?.ticketId}
                    &nbsp;
                  </span>
                  <span className="seperator"></span>
                  <span className="meta-label">
                    {item?.customer?.name ??
                      item?.ticket?.title ??
                      item?.resource?.title}
                  </span>

                  {item.type !== HISTORY_TYPES_ENUMS.Procedure &&
                    (isConnectionHistory ? (
                      <>
                        {" "}
                        <span className="seperator"></span>
                        {hasMultipleAssets ? (
                          <>
                            <div
                              className="meta-label cursor-pointer"
                              data-tip
                              data-for={assetsTooltipId}
                            >
                              {`${ticketAssets.length} ${
                                messages?.connections?.single?.tabs?.assets ??
                                "Assets"
                              }`}
                            </div>
                            <Tooltip
                              id={assetsTooltipId}
                              place="top"
                              clickable
                              delayHide={100}
                              getContent={() => (
                                <WorkOrderAssetsTooltipContent
                                  assets={ticketAssets}
                                />
                              )}
                            />
                          </>
                        ) : singleAssetName ? (
                          <>
                            {canOpen(
                              `/app/asset-hub/my-assets/${singleAssetId}`,
                            ) ? (
                              <BodyText
                                size={BODY_TEXT_SIZES.X_SMALL}
                                color="text-brand"
                                className="cursor-pointer flex items-center z-[4]"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  navigate(
                                    `/app/asset-hub/my-assets/${singleAssetId}`,
                                  );
                                }}
                              >
                                {singleAssetName}
                              </BodyText>
                            ) : (
                              <span className="meta-label">
                                {singleAssetName}
                              </span>
                            )}
                            <span className="seperator"></span>
                            <span className="meta-label">
                              {singleAssetSerial}
                            </span>
                          </>
                        ) : (
                          <span className="meta-label">
                            {singleAssetFallbackLabel}
                          </span>
                        )}
                        {subWorkOrderCount > 0 && (
                          <>
                            <span className="seperator"></span>
                            <div className="meta-label">
                              {`${subWorkOrderCount} ${subWorkOrderLabel}`}
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        {" "}
                        <span className="seperator"></span>{" "}
                        {hasMultipleAssets ? (
                          <>
                            <div
                              className="meta-label cursor-pointer"
                              data-tip
                              data-for={assetsTooltipId}
                            >
                              {`${ticketAssets.length} ${
                                messages?.connections?.single?.tabs?.assets ??
                                "Assets"
                              }`}
                            </div>
                            <Tooltip
                              id={assetsTooltipId}
                              place="top"
                              clickable
                              delayHide={100}
                              getContent={() => (
                                <WorkOrderAssetsTooltipContent
                                  assets={ticketAssets}
                                />
                              )}
                            />
                          </>
                        ) : (
                          <div className="meta-label">
                            {ticketAssets?.[0]?.name ||
                              item?.machineDetails?.name ||
                              machine?.name}
                          </div>
                        )}
                        {subWorkOrderCount > 0 && (
                          <>
                            <span className="seperator"></span>
                            <div className="meta-label">
                              {`${subWorkOrderCount} ${subWorkOrderLabel}`}
                            </div>
                          </>
                        )}
                      </>
                    ))}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {item?.type !== HISTORY_TYPES_ENUMS.Procedure ? (
        <Tag
          label={status?.label}
          style={{
            color: isHovered ? COLOR.$white_v2 : status.color,
            backgroundColor: isHovered
              ? status.color
              : generateStatusBackgroundColor(status.color),
          }}
        />
      ) : (
        <Tag
          className={item?.status}
          label={item?.resource?.state}
          color={TAG_COLORS.GREEN}
        />
      )}
    </Wrapper>
  );
};

const HistoryNoteItem = ({
  msg,
  item,
  machine,
  onDelete = () => {},
  processAttachment = async () => {},
  isSharedAsset = false,
}) => {
  const { messages } = useIntl();

  const [showMenu, setShowMenu] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [isEditorScrolling, setIsEditorScrolling] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAiNoteOvrelay, setShowAiNoteOvrelay] = useState(false);
  const setImagePreview = useChatStore((s) => s.setImagePreview);

  const { user } = useAuth();
  const { isOemAdmin } = useRoleManager(user?.role);
  const isSelfCreated = item?.createdBy?._id === user._id;
  // Every note change needs Update Assets, as adding one does. With it,
  // custom roles edit any note; deleting stays with its author (and Admins),
  // as for built-in roles.
  const { can } = usePermissions();
  const canChangeNotes = can(PERMISSIONS.assets.updateHistory);
  const canEditNote =
    canChangeNotes &&
    (isOemAdmin || isSelfCreated || user?.role === Role.CUSTOM);
  const canDeleteNote = canChangeNotes && (isOemAdmin || isSelfCreated);

  const dateFormat = user?.dateFormat || "DD-MM-YYYY";
  const timeFormat = user?.timeFormat || "hh:mm A";

  const { mutation: updateMachineHistoryNote } = useUpdateMachineHistoryNote();

  const noteText = useRef("");
  const aiIconRef = React.useRef();

  const onActionMenuClose = () => {
    setShowMenu(false);
  };

  const handleHistoryUpdate = async (
    updatedMessage,
    attachments = [],
    isInternal,
  ) => {
    try {
      setIsSavingNote(true);
      let attachmentLinks = [];
      const newAddedAttachments =
        attachments.filter((attachment) => !!attachment.path) ?? [];
      if (newAddedAttachments.length) {
        attachmentLinks = await processAttachment(
          attachments.filter((attachment) => !!attachment.path) ?? [],
        );
      }
      await updateMachineHistoryNote({
        note: {
          message: updatedMessage,
          attachments: [
            ...(attachments
              .filter((attachment) => !!attachment.url)
              .map(({ name, size, type, url }) => ({
                name,
                size,
                type,
                url,
              })) ?? []),
            ...attachmentLinks,
          ],
          isInternal,
        },
        machine: machine?._id,
        _id: item._id,
      });

      toast(
        <Toast
          message={
            msg?.machines?.machineDetails.historyTab.noteMessages.updateSuccess
          }
        />,
        {
          closeButton: false,
        },
      );
      setIsEditMode(false);
    } catch (error) {
      toast(<Toast message={error.message} type={TOAST_TYPES.ERROR} />, {
        closeButton: false,
      });
    } finally {
      setIsSavingNote(false);
    }
  };

  const handleEditClick = () => {
    setIsEditMode(true);
    setShowMenu(false);
  };

  const handleDeleteClick = () => {
    onDelete();
    setShowMenu(false);
  };

  return (
    <>
      {!isEditMode ? (
        <div className="history-note-item-container">
          <div className="history-note-read-mode !gap-md">
            <span className="ticket-icon history">
              <InternalMachineHistoryNoteIcon className="h-3xl w-3xl m-auto" />
              {!item.note.isInternal && (
                <ExternalIcon
                  size={EXTERNAL_ICON_SIZES.ACCORDION}
                  className={EXTERNAL_ICON_THUMB_BADGE_CLASS}
                />
              )}
            </span>
            <div className="history-note-read-mode-details-container">
              <div className="flex items-center w-full">
                <div className="flex items-center">
                  <BodyText
                    size={BODY_TEXT_SIZES.X_SMALL}
                    color="text-secondary"
                  >
                    {[
                      moment(item.createdAt).format(dateFormat),
                      moment(item.createdAt).format(timeFormat),
                      item.createdBy?.name,
                      item?.machineDetails?.name,
                      item?.machineDetails?.serialNumber,
                    ]
                      .filter(Boolean)
                      .join(" \u2022 ")}
                  </BodyText>
                </div>
                <div className="flex items-center ml-auto gap-x-2xs">
                  {isEditorScrolling && (
                    <>
                      {isExpanded ? (
                        <ArrowsInSimple
                          size={16}
                          className="u-text-color-gray-v2-60 u-margin-r-3 u-cursor-pointer"
                          onClick={() => setIsExpanded(false)}
                        />
                      ) : (
                        <ArrowsOutSimple
                          size={16}
                          className="u-text-color-gray-v2-60 u-margin-r-3 u-cursor-pointer"
                          onClick={() => setIsExpanded(true)}
                        />
                      )}
                    </>
                  )}
                  {item.type === HISTORY_TYPES_ENUMS.AI && (
                    <>
                      <div
                        onMouseEnter={() => setShowAiNoteOvrelay(true)}
                        onMouseLeave={() => setShowAiNoteOvrelay(false)}
                        ref={aiIconRef}
                        className="flex"
                      >
                        <AiSparklesIcon className="h-3xl w-3xl" />
                      </div>
                      {showAiNoteOvrelay && (
                        <Overlay
                          arrowClasses="before:bg-secondary before:shadow-xl"
                          className="bg-secondary rounded-md py-2xs px-sm shadow-xl w-fit z-10 max-w-96xl"
                          containerRef={document.body}
                          placement={bottom}
                          offset={[0, 8]}
                          showOverlay={showAiNoteOvrelay}
                          targetRef={aiIconRef}
                        >
                          <div
                            className="relative"
                            onMouseEnter={() => setShowAiNoteOvrelay(true)}
                            onMouseLeave={() => setShowAiNoteOvrelay(false)}
                          >
                            <BodyText
                              className="!text-inverse"
                              size={BODY_TEXT_SIZES.X_SMALL}
                            >
                              {
                                messages["aiNotetaker"][
                                  "history-note-overlay-text"
                                ]
                              }
                            </BodyText>
                          </div>
                        </Overlay>
                      )}
                    </>
                  )}
                  {(canEditNote || canDeleteNote) && !isSharedAsset && (
                    <div
                      className="flex ml-auto cursor-pointer"
                      onClick={() => {
                        setShowMenu(true);
                      }}
                    >
                      <DotsThree size={20} />
                      {showMenu && (
                        <div className="u-relative">
                          <span className="parts-popup__container">
                            <PopupMenuV2
                              onDeleteClick={handleDeleteClick}
                              onEditClick={handleEditClick}
                              editLabel={messages?.common?.edit}
                              deleteLabel={messages?.common?.delete}
                              editLeadingIcon={
                                <PencilSimple className="w-lg h-lg flex-shrink-0" />
                              }
                              deleteLeadingIcon={
                                <Trash className="w-lg h-lg flex-shrink-0 text-danger" />
                              }
                              labelClassName="flex items-center gap-xs"
                              close={onActionMenuClose}
                              showEditButton={canEditNote}
                              showDeleteButton={canDeleteNote}
                            />
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <TextEditor
                keyId="note-detail-closed"
                mainEditorClassName={`${
                  isExpanded ? "max-h-max" : "overflow-y-hidden"
                }`}
                placeholder={
                  item?.note.attachments?.length > 0
                    ? " "
                    : msg.machines.machineDetails.historyTab.addNote
                }
                content={item?.note?.message ?? ""}
                onUpdate={() => {}}
                readOnly
                setIsScrolling={setIsEditorScrolling}
              />
              {item?.note.attachments?.length > 0 && (
                <>
                  <EmptySpace size="16px" />
                  <div className="history-note-read-mode-attachments-container">
                    {item?.note.attachments.map((attachment) => {
                      return (
                        <Fragment key={attachment.url}>
                          {attachment.type.includes("pdf") ? (
                            <div
                              className="history-note-read-mode-pdf-attachment u-cursor-pointer"
                              onClick={() =>
                                window.open(attachment.url, "_blank")
                              }
                            >
                              <div className="icon-wrapper">
                                <PdfColoredIcon className="icon" />
                              </div>
                              <Label
                                size={LABEL_SIZES.X_SMALL}
                                color="text-secondary"
                                className="font-medium truncate mx-2xs"
                              >
                                {attachment.name}
                              </Label>
                            </div>
                          ) : (
                            <img
                              src={attachment.url}
                              className="history-note-read-mode-image-attachment u-cursor-pointer"
                              onClick={(e) => {
                                setImagePreview({
                                  openedImage: e.target.src,
                                  isOpen: true,
                                });
                              }}
                            />
                          )}
                        </Fragment>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="u-width-100">
            <div className="u-width-100 u-relative">
              <TextEditor
                keyId="edit-note-detail"
                placeholder={msg.machines.machineDetails.historyTab.addNote}
                className="flex flex-col-reverse w-full"
                toolbarTopBorder
                showAttachmentButton
                showActionButtons
                autoFocus={true}
                content={item?.note?.message ?? ""}
                defaultAttachments={item?.note.attachments}
                onUpdate={(newValue) => (noteText.current = newValue)}
                handleClose={() => setIsEditMode(false)}
                isSaving={isSavingNote}
                handleSave={async (attachments = [], isCheckboxChecked) => {
                  const updatedMessage = noteText.current
                    ? JSON.stringify(noteText.current)
                    : item?.note?.message;
                  await handleHistoryUpdate(
                    updatedMessage,
                    attachments,
                    !isCheckboxChecked,
                  );
                }}
                checkboxLabel={
                  msg?.machines?.machineDetails.historyTab.showToFacility
                }
                defaultCheckboxValue={!item?.note.isInternal}
              />
            </div>
          </div>
        </>
      )}
    </>
  );
};
